import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { fetchOrderById } from '../lib/api';
import { fetchCustomerOrders } from '../lib/customerApi';
import {
  clearTrackedOrder,
  findActiveOrder,
  getOrderTrackingPath,
  isActiveOrder,
  readTrackedOrder,
  TRACKED_ORDER_EVENT,
  writeTrackedOrder,
} from '../lib/trackedOrder';
import VemmacLogo from '../assets/images/vemmac_logo.jpg';

const navItems = [
  { label: 'Home', to: '/' },
  { label: 'Menu', to: '/menu' },
  { label: 'Services', to: '/services' },
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
];

const MenuIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M4 7h16M4 12h16M4 17h16" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="m6 6 12 12M18 6 6 18" />
  </svg>
);

const AccountIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M20 21a8 8 0 0 0-16 0" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const CartIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M6 6h15l-1.6 8.2a2 2 0 0 1-2 1.6H9.1a2 2 0 0 1-2-1.6L5.3 3H3" />
    <circle cx="9" cy="20" r="1.5" />
    <circle cx="18" cy="20" r="1.5" />
  </svg>
);

const TrackingIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M21 10c0 6-9 12-9 12S3 16 3 10a9 9 0 1 1 18 0Z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const NavBar = () => {
  const { totalQuantity } = useCart();
  const { isAuthenticated, initializing, getToken, logout } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [activeOrder, setActiveOrder] = useState(null);
  const [trackingRefreshKey, setTrackingRefreshKey] = useState(0);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsAccountMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const refresh = () => setTrackingRefreshKey((value) => value + 1);

    window.addEventListener(TRACKED_ORDER_EVENT, refresh);
    window.addEventListener('storage', refresh);

    return () => {
      window.removeEventListener(TRACKED_ORDER_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  useEffect(() => {
    if (initializing) return undefined;

    let cancelled = false;

    const resolveActiveOrder = async () => {
      if (isAuthenticated) {
        try {
          const token = await getToken();
          const orders = await fetchCustomerOrders(token);
          const nextOrder = findActiveOrder(Array.isArray(orders) ? orders : []);

          if (!cancelled) {
            setActiveOrder(nextOrder);
            if (nextOrder) writeTrackedOrder(nextOrder, { notify: false });
          }
        } catch {
          if (!cancelled) setActiveOrder(null);
        }
        return;
      }

      const storedOrder = readTrackedOrder();
      if (!storedOrder || !isActiveOrder(storedOrder)) {
        clearTrackedOrder({ notify: false });
        if (!cancelled) setActiveOrder(null);
        return;
      }

      try {
        const latestOrder = await fetchOrderById(storedOrder.id);
        if (isActiveOrder(latestOrder)) {
          writeTrackedOrder(latestOrder, { notify: false });
          if (!cancelled) setActiveOrder(latestOrder);
        } else {
          clearTrackedOrder({ notify: false });
          if (!cancelled) setActiveOrder(null);
        }
      } catch {
        if (!cancelled) setActiveOrder(storedOrder);
      }
    };

    resolveActiveOrder();

    return () => {
      cancelled = true;
    };
  }, [getToken, initializing, isAuthenticated, location.pathname, location.search, trackingRefreshKey]);

  const activeOrderPath = getOrderTrackingPath(activeOrder);

  const handleLogout = async () => {
    setIsAccountMenuOpen(false);
    await logout();
  };

  return (
    <header className="site-header">
      <div className="header-top-row">
        <button
          type="button"
          className="nav-mobile-toggle"
          onClick={() => setIsMobileMenuOpen((prev) => !prev)}
          aria-expanded={isMobileMenuOpen}
          aria-controls="primary-nav"
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
        >
          {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
        </button>

        <NavLink to="/" className="header-brand" aria-label="Go to home">
          <img src={VemmacLogo} alt="Vemmac Kitchen Logo" className="logo-image" />
          <div className="logo-text">Vemmac Kitchen</div>
        </NavLink>

        <div className="nav-cta">
          {activeOrderPath && (
            <NavLink
              to={activeOrderPath}
              className={({ isActive }) => `nav-tracking-link nav-tracking-desktop${isActive ? ' active' : ''}`}
            >
              <TrackingIcon />
              <span>Order Tracking</span>
            </NavLink>
          )}

          {!initializing && (
            isAuthenticated ? (
              <div className="nav-account">
                <button
                  type="button"
                  className="nav-profile-link nav-profile-button"
                  onClick={() => setIsAccountMenuOpen((prev) => !prev)}
                  aria-expanded={isAccountMenuOpen}
                  aria-haspopup="menu"
                  aria-label="Account menu"
                >
                  <AccountIcon />
                </button>
                {isAccountMenuOpen && (
                  <div className="nav-account-menu" role="menu">
                    <Link to="/profile" role="menuitem">Profile</Link>
                    <Link to="/profile?view=orders" role="menuitem">Order History</Link>
                    <button type="button" onClick={handleLogout} role="menuitem">
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <span className="nav-auth-links">
                <Link to="/account">Login</Link>
              </span>
            )
          )}

          <NavLink
            to="/cart"
            className={({ isActive }) => `nav-cart-btn${isActive ? ' active' : ''}`}
            aria-label="View cart"
          >
            <span aria-hidden="true" className="nav-cart-icon"><CartIcon /></span>
            {totalQuantity > 0 && <span className="cart-pill">{totalQuantity}</span>}
          </NavLink>
        </div>
      </div>

      <nav
        id="primary-nav"
        className={`nav-items${isMobileMenuOpen ? ' open' : ''}`}
        aria-label="Primary"
      >
        {navItems.map((item) => (
          <NavLink
            key={item.label}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            {item.label}
          </NavLink>
        ))}

        {activeOrderPath && (
          <NavLink
            to={activeOrderPath}
            className={({ isActive }) => `nav-link nav-tracking-mobile${isActive ? ' active' : ''}`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Order Tracking
          </NavLink>
        )}
      </nav>
    </header>
  );
};

export default NavBar;
